/**
 * Pure helpers for the Quality Check wall (JARVIS-128): curriculum roster
 * resolution, the class-derived filter predicates plus the competition scope,
 * faceted option counts, the decided/open state of a piece, the five-class
 * review window, the day → class grouping, and the userSettings filter-shape
 * parser. No React, no Firebase.
 *
 * The class axes resolve THROUGH the class the artwork was made in
 * (`artwork.classId` → `ClassConstructor`): weekday from `startDate`, teacher
 * from `teacherIds`, curriculum from `curriculumId`. The scope axis resolves
 * through the student's `competitionSubmissions` instead — see
 * `buildCompetitionEntryIndex`.
 */
import dayjs from "dayjs"
import { isPlainObject, keyBy, maxBy, sortBy, uniq, uniqBy } from "lodash"

import { dedupeLatestVersions } from "#components/ArtworkWall/artworkWallHelpers"
import { type ArtworkWallItem } from "#components/ArtworkWall/artworkWallTypes"
import {
  getArtworkSemester,
  isArtworkDontShare,
  isClassImageShared,
} from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import {
  getCompetitionName,
  getCompetitionSlotLabel,
} from "#constants/competitionOptions"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { type CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import {
  type StudentArtwork,
  type StudentConstructor,
} from "#customTypes/composedTypes/studentTypes"
import { type UserConstructor } from "#customTypes/composedTypes/userTypes"
import {
  type QualityCheckFiltersSetting,
  type QualityCheckScope,
  type Uuid,
} from "#customTypes/globalTypes"
import { formatDob, STUDENT_DOB_FORMAT } from "#utils/dateHelpers"
import { getCurrentSemester } from "#utils/semesterHelpers"

// ---------------------------------------------------------------------------
// Roster resolution
// ---------------------------------------------------------------------------

export interface CurriculumRoster {
  /** Sorted, deduped ids of students enrolled in a class on the curriculum. */
  studentIds: Uuid[]
  /** The classes that contributed to `studentIds`. */
  classIds: Uuid[]
}

/**
 * Enrollment for one curriculum: every class on it, and every student on
 * those classes. No skill-level narrow — the curriculum is the plan the
 * classes teach, so its roster is exactly who is in those classes.
 */
export const resolveCurriculumRoster = (
  classes: readonly ClassConstructor[],
  curriculumId: Uuid
): CurriculumRoster => {
  const classIds: Uuid[] = []
  const studentIds: Uuid[] = []
  for (const classObj of classes) {
    if (classObj.curriculumId !== curriculumId) continue
    classIds.push(classObj.id)
    studentIds.push(...(classObj.studentIds ?? []))
  }
  return {
    studentIds: uniq(studentIds).sort(),
    classIds: uniq(classIds).sort(),
  }
}

export interface QualityCheckCurriculumOption {
  id: Uuid
  name: string
}

/**
 * The curriculum dropdown: every curriculum that has a class this semester,
 * by name. A class whose curriculum doc did not load contributes nothing —
 * there is no name to show for it.
 */
export const buildCurriculumCatalog = (
  classes: readonly Pick<ClassConstructor, "curriculumId">[],
  curriculaById: Record<Uuid, Pick<CurriculumConstructor, "id" | "name">>
): QualityCheckCurriculumOption[] =>
  sortBy(
    uniq(classes.map((classObj) => classObj.curriculumId))
      .filter((id) => id && curriculaById[id])
      .map((id) => ({ id, name: curriculaById[id].name })),
    (option) => option.name.toLowerCase(),
    (option) => option.id
  )

/** Class ids referenced by the students' artwork that are not yet loaded. */
export const getMissingArtworkClassIds = (
  students: readonly StudentConstructor[],
  loadedClassIds: ReadonlySet<Uuid>
): Uuid[] => {
  const missing: Uuid[] = []
  for (const student of students) {
    for (const artwork of Object.values(student.artwork ?? {})) {
      if (artwork.classId && !loadedClassIds.has(artwork.classId)) {
        missing.push(artwork.classId)
      }
    }
  }
  return uniq(missing).sort()
}

// ---------------------------------------------------------------------------
// Class-derived attributes
// ---------------------------------------------------------------------------

/**
 * Weekday index (0 = Sunday … 6 = Saturday) of the class's first session.
 * Classes store `startDate` as `M/D/YY`; `dayjs(startDate)` is how the rest of
 * the app (e.g. `classes/Class.ts`) derives the weekday, so the same parse is
 * used here. Unparseable → null.
 */
export const getClassWeekday = (
  classObj: Pick<ClassConstructor, "startDate">
): number | null => {
  if (!classObj.startDate) return null
  const parsed = dayjs(classObj.startDate)
  return parsed.isValid() ? parsed.day() : null
}

export const describeWeekday = (weekday: number): string =>
  dayjs().day(weekday).format("dddd")

/**
 * Who taught the class: `teacherIds` (aggregated from sessions), falling back
 * to `defaultTeacherId` when no session ever recorded a teacher.
 */
export const getClassTeacherIds = (
  classObj: Pick<ClassConstructor, "teacherIds" | "defaultTeacherId">
): Uuid[] => {
  const fromSessions = (classObj.teacherIds ?? []).filter(Boolean)
  if (fromSessions.length > 0) return uniq(fromSessions)
  return classObj.defaultTeacherId ? [classObj.defaultTeacherId] : []
}

/**
 * Minutes from midnight for a class `startTime` (`h:mm a`, e.g. "10:00 am").
 * Blank or unreadable → null, so those classes sort last.
 */
export const getClassStartMinutes = (
  startTime: string | null | undefined
): number | null => {
  const match = (startTime ?? "")
    .trim()
    .toLowerCase()
    .match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/)
  if (!match) return null
  let hour = Number(match[1])
  const minute = Number(match[2] ?? "0")
  const meridiem = match[3]
  if (minute > 59) return null
  if (meridiem) {
    if (hour < 1 || hour > 12) return null
    if (meridiem === "am" && hour === 12) hour = 0
    if (meridiem === "pm" && hour !== 12) hour += 12
  } else if (hour > 23) {
    return null
  }
  return hour * 60 + minute
}

// ---------------------------------------------------------------------------
// Competition entries
// ---------------------------------------------------------------------------

export interface CompetitionEntry {
  competitionId: string
  /** Box position within the competition; null for pre-slot legacy records. */
  slotIndex: number | null
}

export type CompetitionEntriesByArtworkId = Record<string, CompetitionEntry[]>

type StudentForCompetitions = Pick<
  StudentConstructor,
  "artwork" | "competitionSubmissions"
>

/**
 * Every competition box each piece occupies, keyed by artwork id.
 *
 * Membership lives on the STUDENT doc (`competitionSubmissions`: one
 * null-padded slot array per competition, index = box, value = the occupying
 * artworkId), not on the piece. That is what makes a family pair
 * discoverable from Jarvis: a parent's scanned piece is registered under
 * their CHILD's student doc, so both boxes of the pair sit in one array and
 * `slotIndex` says which half of the pair a piece is.
 *
 * The legacy per-piece `competitionIds` / `competitionId` fields are folded
 * in with no slot, so entries written before slots existed stay visible.
 */
export const buildCompetitionEntryIndex = (
  students: readonly StudentForCompetitions[]
): CompetitionEntriesByArtworkId => {
  const index: CompetitionEntriesByArtworkId = {}

  const add = (artworkId: string, entry: CompetitionEntry) => {
    const existing = index[artworkId]
    if (!existing) {
      index[artworkId] = [entry]
      return
    }
    const alreadyKnown = existing.some(
      (known) => known.competitionId === entry.competitionId
    )
    if (!alreadyKnown) existing.push(entry)
  }

  for (const student of students) {
    for (const [competitionId, slots] of Object.entries(
      student.competitionSubmissions ?? {}
    )) {
      slots.forEach((artworkId, slotIndex) => {
        if (artworkId) add(artworkId, { competitionId, slotIndex })
      })
    }
    for (const artwork of Object.values(student.artwork ?? {})) {
      const legacyIds = [
        ...(artwork.competitionIds ?? []),
        ...(artwork.competitionId ? [artwork.competitionId] : []),
      ]
      for (const competitionId of legacyIds) {
        add(artwork.id, { competitionId, slotIndex: null })
      }
    }
  }

  return index
}

/**
 * The scope radio: everything, non-competition artwork only, or competition
 * entries only. A scope, not a per-competition dropdown, by owner decision
 * (Sep 2026): one competition runs at a time, so "which competition" was a
 * dropdown's worth of chrome for a binary question.
 */
export const QUALITY_CHECK_SCOPE = {
  ALL: "all",
  ARTWORK: "artwork",
  COMPETITION: "competition",
} as const satisfies Record<string, QualityCheckScope>

/**
 * The semester dropdown's "everything" sentinel.
 * SYNC: the "Show All" option value in `SemesterOptions`
 * (client/src/components/forms/SemesterDropdown.tsx).
 */
export const ALL_SEMESTERS_FILTER_VALUE = "All"

/**
 * Semester scoping happens on the PIECE (`artwork.semester`, falling back to
 * the semester `timeCreated` lands in — `getArtworkSemester`), not on the
 * roster read: the roster is always current-semester enrollment, but a
 * student's artwork map spans their whole history, which is exactly why an
 * unscoped surface showed years of work under a current-semester banner.
 */
export const matchesSemester = (
  item: ArtworkWallItem,
  semester: string
): boolean =>
  semester === ALL_SEMESTERS_FILTER_VALUE ||
  getArtworkSemester(item.artwork) === semester

// Keyed off `artwork.id`, not the tile's `id`: the wall only promises the tile
// key is "normally" the artwork id, and the index is built from artwork.
const entriesFor = (
  item: ArtworkWallItem,
  entriesByArtworkId: CompetitionEntriesByArtworkId
): CompetitionEntry[] => entriesByArtworkId[item.artwork.id] ?? []

const hasCompetitionEntry = (
  item: ArtworkWallItem,
  entriesByArtworkId: CompetitionEntriesByArtworkId
): boolean => entriesFor(item, entriesByArtworkId).length > 0

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

/**
 * The live filter state: the two class axes (`null` = All) plus the scope.
 * The curriculum is not here: it drives the read itself.
 */
export interface QualityCheckFilters {
  weekday: number | null
  teacherId: Uuid | null
  scope: QualityCheckScope
}

export const EMPTY_QUALITY_CHECK_FILTERS: QualityCheckFilters = {
  weekday: null,
  teacherId: null,
  scope: QUALITY_CHECK_SCOPE.ALL,
}

export const hasActiveFilters = (filters: QualityCheckFilters): boolean =>
  filters.weekday !== null ||
  filters.teacherId !== null ||
  filters.scope !== QUALITY_CHECK_SCOPE.ALL

type ClassesById = Record<Uuid, ClassConstructor>

const classOf = (
  item: ArtworkWallItem,
  classesById: ClassesById
): ClassConstructor | undefined =>
  item.artwork.classId ? classesById[item.artwork.classId] : undefined

/**
 * Each predicate is "All" when its value is null. A piece whose class record
 * cannot be found matches only "All": there is nothing to resolve the
 * attribute through, and inventing a bucket for it would be a schema change
 * in disguise.
 */
export const matchesWeekday = (
  item: ArtworkWallItem,
  classesById: ClassesById,
  weekday: number | null
): boolean => {
  if (weekday === null) return true
  const classObj = classOf(item, classesById)
  return classObj !== undefined && getClassWeekday(classObj) === weekday
}

export const matchesTeacher = (
  item: ArtworkWallItem,
  classesById: ClassesById,
  teacherId: Uuid | null
): boolean => {
  if (teacherId === null) return true
  const classObj = classOf(item, classesById)
  return (
    classObj !== undefined && getClassTeacherIds(classObj).includes(teacherId)
  )
}

/**
 * The selected curriculum, applied to the roster students' pieces: a piece
 * made in a class on another plan (the same student's sculpture class, last
 * semester's class) is not this curriculum's work. A piece with no class
 * record stays — a class-less mobile upload or competition entry has no
 * other plan to belong to.
 */
export const matchesCurriculum = (
  item: ArtworkWallItem,
  classesById: ClassesById,
  curriculumId: Uuid
): boolean => {
  const classObj = classOf(item, classesById)
  return classObj === undefined || classObj.curriculumId === curriculumId
}

/**
 * The one axis that does NOT resolve through the class: competition
 * membership hangs off the student doc, so a mobile-uploaded piece with no
 * class record still matches COMPETITION (it is exactly how a parent's entry
 * arrives). ARTWORK is its complement; ALL matches everything.
 */
export const matchesScope = (
  item: ArtworkWallItem,
  entriesByArtworkId: CompetitionEntriesByArtworkId,
  scope: QualityCheckScope
): boolean => {
  if (scope === QUALITY_CHECK_SCOPE.ALL) return true
  const hasEntry = hasCompetitionEntry(item, entriesByArtworkId)
  return scope === QUALITY_CHECK_SCOPE.COMPETITION ? hasEntry : !hasEntry
}

export const matchesQualityCheckFilters = (
  item: ArtworkWallItem,
  classesById: ClassesById,
  entriesByArtworkId: CompetitionEntriesByArtworkId,
  filters: QualityCheckFilters
): boolean =>
  matchesWeekday(item, classesById, filters.weekday) &&
  matchesTeacher(item, classesById, filters.teacherId) &&
  matchesScope(item, entriesByArtworkId, filters.scope)

export const applyQualityCheckFilters = (
  items: readonly ArtworkWallItem[],
  classesById: ClassesById,
  entriesByArtworkId: CompetitionEntriesByArtworkId,
  filters: QualityCheckFilters
): ArtworkWallItem[] =>
  items.filter((item) =>
    matchesQualityCheckFilters(item, classesById, entriesByArtworkId, filters)
  )

/** Distinct students represented in a set of items. */
export const countStudents = (items: readonly ArtworkWallItem[]): number =>
  new Set(items.map((item) => item.studentId)).size

/** Pieces that only ever match "All" because their class record is missing. */
export const countUnresolvedClassItems = (
  items: readonly ArtworkWallItem[],
  classesById: ClassesById
): number => items.filter((item) => !classOf(item, classesById)).length

// ---------------------------------------------------------------------------
// Faceted options
// ---------------------------------------------------------------------------

export interface QualityCheckOption<V extends string | number> {
  value: V
  label: string
  /** Pieces this option would show given the OTHER filters as they stand. */
  count: number
}

export interface QualityCheckFilterOptions {
  weekdays: QualityCheckOption<number>[]
  teachers: QualityCheckOption<Uuid>[]
  /** Pieces each scope radio would show, given the two class axes. */
  scopeCounts: Record<QualityCheckScope, number>
}

export interface QualityCheckJoins {
  classesById: ClassesById
  usersById: Record<Uuid, Pick<UserConstructor, "id" | "name">>
  competitionEntriesByArtworkId: CompetitionEntriesByArtworkId
}

const countWhere = (
  items: readonly ArtworkWallItem[],
  predicate: (item: ArtworkWallItem) => boolean
): number => items.reduce((n, item) => (predicate(item) ? n + 1 : n), 0)

/**
 * Options are drawn from the artwork VISIBLE under the other active filters,
 * not from everything loaded — an option that could only ever yield zero
 * pieces disappears instead of listing as "(0)". (Owner report, Sep 2026:
 * under the Competition scope every class-derived dropdown listed the whole
 * school's teachers at (0), because the options came from regular artwork the
 * scope had filtered out.) The one exception is a CURRENTLY-SELECTED value:
 * its option stays pinned even at zero, both so the Select keeps showing its
 * label rather than a raw id and so the user can see — and undo — the choice
 * that emptied the surface. `useGetTeachers` is deliberately NOT the teacher
 * source: it returns every non-disabled user, not teachers. Counts stay
 * faceted: each option's count applies the other axes but never its own, so
 * the row always tells the user what picking it would do. Counts are pieces
 * across the whole curriculum, not just the five-class window.
 */
export const buildQualityCheckOptions = (
  items: readonly ArtworkWallItem[],
  joins: QualityCheckJoins,
  filters: QualityCheckFilters
): QualityCheckFilterOptions => {
  const { classesById, usersById } = joins
  const entries = joins.competitionEntriesByArtworkId

  const exceptWeekday = items.filter(
    (item) =>
      matchesTeacher(item, classesById, filters.teacherId) &&
      matchesScope(item, entries, filters.scope)
  )
  const exceptTeacher = items.filter(
    (item) =>
      matchesWeekday(item, classesById, filters.weekday) &&
      matchesScope(item, entries, filters.scope)
  )
  const exceptScope = items.filter(
    (item) =>
      matchesWeekday(item, classesById, filters.weekday) &&
      matchesTeacher(item, classesById, filters.teacherId)
  )

  const weekdaySet = new Set<number>()
  const teacherSet = new Set<Uuid>()
  for (const item of exceptWeekday) {
    const classObj = classOf(item, classesById)
    if (!classObj) continue
    const weekday = getClassWeekday(classObj)
    if (weekday !== null) weekdaySet.add(weekday)
  }
  for (const item of exceptTeacher) {
    const classObj = classOf(item, classesById)
    if (!classObj) continue
    getClassTeacherIds(classObj).forEach((id) => teacherSet.add(id))
  }

  // Pin the active selections so they stay visible (and undoable) at zero.
  if (filters.weekday !== null) weekdaySet.add(filters.weekday)
  if (filters.teacherId !== null) teacherSet.add(filters.teacherId)

  const weekdays = sortBy([...weekdaySet]).map((weekday) => ({
    value: weekday,
    label: describeWeekday(weekday),
    count: countWhere(exceptWeekday, (item) =>
      matchesWeekday(item, classesById, weekday)
    ),
  }))

  const teachers = sortBy(
    [...teacherSet].map((teacherId) => ({
      value: teacherId,
      label: usersById[teacherId]?.name ?? "Unknown teacher",
      count: countWhere(exceptTeacher, (item) =>
        matchesTeacher(item, classesById, teacherId)
      ),
    })),
    (option) => option.label.toLowerCase(),
    (option) => option.value
  )

  const scopeCounts: Record<QualityCheckScope, number> = {
    [QUALITY_CHECK_SCOPE.ALL]: exceptScope.length,
    [QUALITY_CHECK_SCOPE.ARTWORK]: countWhere(exceptScope, (item) =>
      matchesScope(item, entries, QUALITY_CHECK_SCOPE.ARTWORK)
    ),
    [QUALITY_CHECK_SCOPE.COMPETITION]: countWhere(exceptScope, (item) =>
      matchesScope(item, entries, QUALITY_CHECK_SCOPE.COMPETITION)
    ),
  }

  return { weekdays, teachers, scopeCounts }
}

// ---------------------------------------------------------------------------
// Decided / open
// ---------------------------------------------------------------------------

/**
 * A piece is decided once it is shared to the family or marked Don't share.
 * Shared uses the class-day rule (Don't share wins over a stored `isShared`),
 * so a piece is never both.
 */
export const QUALITY_CHECK_DECISION = {
  SHARED: "shared",
  BLOCKED: "blocked",
  OPEN: "open",
} as const

export type QualityCheckDecision =
  (typeof QUALITY_CHECK_DECISION)[keyof typeof QUALITY_CHECK_DECISION]

type DecisionFields = Pick<StudentArtwork, "isShared" | "dontShare">

export const getQualityCheckDecision = (
  artwork: DecisionFields
): QualityCheckDecision => {
  if (isClassImageShared(artwork)) return QUALITY_CHECK_DECISION.SHARED
  if (isArtworkDontShare(artwork)) return QUALITY_CHECK_DECISION.BLOCKED
  return QUALITY_CHECK_DECISION.OPEN
}

export const isQualityCheckDecided = (artwork: DecisionFields): boolean =>
  getQualityCheckDecision(artwork) !== QUALITY_CHECK_DECISION.OPEN

export type QualityCheckSectionChoice = "open" | "shared" | "blocked" | "mixed"

/**
 * Card color for a section. Open while any piece is undecided. Red when
 * every piece is don't-share. Green when the decided pieces are all shared,
 * or when they used both.
 */
export const getQualityCheckSectionChoice = (
  items: readonly { artwork: DecisionFields }[]
): QualityCheckSectionChoice => {
  if (items.length === 0) return "open"
  if (items.some((item) => !isQualityCheckDecided(item.artwork))) return "open"
  if (items.every((item) => isArtworkDontShare(item.artwork))) return "blocked"
  if (items.every((item) => isClassImageShared(item.artwork))) return "shared"
  return "mixed"
}

/**
 * The session a piece belongs to when `classSessionId` was never written.
 * Keep one already stored. Otherwise the latest session of `classId` whose
 * `eventStart` is at or before `timeCreated` — a Friday upload for a Sunday
 * class lands on the previous Sunday, not the Sunday still ahead. No class,
 * no timestamp, or no session in the past → null.
 */
export const resolvePastClassSessionId = (
  artwork: {
    classId?: string
    classSessionId?: string
    timeCreated?: number
  },
  sessionsByClassId: Record<
    string,
    readonly { id: string; eventStart: string }[]
  >
): string | null => {
  if (artwork.classSessionId) return artwork.classSessionId
  if (!artwork.classId || artwork.timeCreated == null) return null
  const createdAtMs = artwork.timeCreated
  if (!Number.isFinite(createdAtMs)) return null
  const sessions = sessionsByClassId[artwork.classId]
  if (!sessions?.length) return null
  const eligible = sessions.filter((session) => {
    const start = dayjs(session.eventStart).valueOf()
    return Number.isFinite(start) && start <= createdAtMs
  })
  const latest = maxBy(eligible, (session) =>
    dayjs(session.eventStart).valueOf()
  )
  return latest?.id ?? null
}

/**
 * What Share remaining writes: undecided pieces that can sit on a class-day
 * card. A piece already shared or Don't share is left alone.
 * `buildArtworkSharedUpdate` still has the final say per piece.
 */
export const getShareAllTargets = (
  items: readonly ArtworkWallItem[]
): ArtworkWallItem[] =>
  items.filter(
    (item) =>
      !isQualityCheckDecided(item.artwork) &&
      !!item.artwork.classSessionId &&
      item.artwork.isHidden !== true &&
      !isArtworkDontShare(item.artwork)
  )

/** What Don't share remaining writes: undecided pieces only. */
export const getDontShareAllTargets = (
  items: readonly ArtworkWallItem[]
): ArtworkWallItem[] =>
  items.filter((item) => !isQualityCheckDecided(item.artwork))

// ---------------------------------------------------------------------------
// The five-class window
// ---------------------------------------------------------------------------

export const QUALITY_CHECK_CLASS_WINDOW = 5

/**
 * Newest-first walk: every class is included until five OPEN (not fully
 * decided) classes are in. A decided class before that cutoff stays, so a
 * class that was just finished does not jump off the screen; nothing after
 * the cutoff renders, so its images never mount.
 */
export function selectQualityCheckClassWindow(
  classIdsNewestFirst: readonly string[],
  isDecided: (classId: string) => boolean
): string[] {
  const selected: string[] = []
  let openCount = 0
  for (const classId of classIdsNewestFirst) {
    if (openCount >= QUALITY_CHECK_CLASS_WINDOW) break
    selected.push(classId)
    if (!isDecided(classId)) openCount += 1
  }
  return selected
}

// ---------------------------------------------------------------------------
// Grouping: day → class, plus the competition section
// ---------------------------------------------------------------------------

export const QUALITY_CHECK_COMPETITION_SECTION_KEY = "competition"

export interface QualityCheckClassSection {
  /** `${dayKey}|${classId}` (classId "" for no class). Unique on the page. */
  key: string
  dayKey: string
  classId: Uuid | null
  label: string
  /** Every piece in the section, newest first. Share remaining acts on these. */
  allItems: ArtworkWallItem[]
  /** What renders: `allItems`, minus decided pieces when Hide finished items trims a day that still has open work. */
  items: ArtworkWallItem[]
  decided: boolean
}

export interface QualityCheckDayGroup {
  /** YYYY-MM-DD, or "" for a piece with no usable `timeCreated`. */
  key: string
  label: string
  sections: QualityCheckClassSection[]
}

export interface QualityCheckCompetitionSection {
  key: typeof QUALITY_CHECK_COMPETITION_SECTION_KEY
  /** Every entry in the section, newest first. Share remaining acts on these. */
  allItems: ArtworkWallItem[]
  /** What renders. Hide finished items drops decided entries while any are open. */
  items: ArtworkWallItem[]
  decided: boolean
}

export interface QualityCheckLayout {
  competition: QualityCheckCompetitionSection | null
  days: QualityCheckDayGroup[]
  /** Distinct pieces in every rendered section (collapsed ones included). */
  renderedItems: ArtworkWallItem[]
  /** Classes past the window, not rendered. */
  queuedSectionCount: number
  /** "Latest · N versions" counts by item id, from the version dedupe. */
  versionCounts: Record<string, number>
}

export interface QualityCheckLayoutInput {
  /** Semester- and curriculum-scoped pieces (`matchesSemester`, `matchesCurriculum`). */
  items: readonly ArtworkWallItem[]
  classesById: ClassesById
  entriesByArtworkId: CompetitionEntriesByArtworkId
  studentLabelById: Record<Uuid, string>
  filters: QualityCheckFilters
  hideFinishedItems: boolean
  hideFinishedClasses: boolean
}

const getArtworkDayKey = (artwork: Pick<StudentArtwork, "timeCreated">) => {
  const created = dayjs(artwork.timeCreated)
  return created.isValid() ? created.format("YYYY-MM-DD") : ""
}

export const describeQualityCheckDay = (dayKey: string): string =>
  dayKey ? dayjs(dayKey).format("dddd, MMM D") : "Undated"

export const describeQualityCheckClass = (
  classObj: Pick<ClassConstructor, "name" | "startTime"> | undefined
): string => {
  if (!classObj) return "No class"
  const startTime = classObj.startTime?.trim()
  return startTime ? `${classObj.name} · ${startTime}` : classObj.name
}

const newestFirst = (items: readonly ArtworkWallItem[]): ArtworkWallItem[] =>
  sortBy(items, (item) => -(item.artwork.timeCreated ?? 0))

/**
 * Within a day: the later class first, a class with no readable start time
 * last, then by name. Days are newest first.
 */
const compareSections = (
  a: QualityCheckClassSection,
  b: QualityCheckClassSection,
  classesById: ClassesById
): number => {
  if (a.dayKey !== b.dayKey) return a.dayKey < b.dayKey ? 1 : -1
  const classA = a.classId ? classesById[a.classId] : undefined
  const classB = b.classId ? classesById[b.classId] : undefined
  const startA = getClassStartMinutes(classA?.startTime)
  const startB = getClassStartMinutes(classB?.startTime)
  if ((startA === null) !== (startB === null)) return startA === null ? 1 : -1
  if (startA !== null && startB !== null && startA !== startB) {
    return startB - startA
  }
  const byName = (classA?.name ?? "").localeCompare(
    classB?.name ?? "",
    undefined,
    { sensitivity: "base" }
  )
  if (byName !== 0) return byName
  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0
}

/** "Child's cow, Parent's cow" — the box name, or the competition name for a legacy no-slot entry. */
const describeEntries = (entries: readonly CompetitionEntry[]): string =>
  entries
    .map((entry) =>
      entry.slotIndex === null
        ? getCompetitionName(entry.competitionId)
        : getCompetitionSlotLabel(entry.competitionId, entry.slotIndex)
    )
    .join(", ")

/**
 * Badges each competition piece with WHOSE it is and which box: the
 * student's dob/name string, then the box name (what tells a family pair's
 * two pieces apart). Order is left alone.
 */
export const badgeCompetitionItems = (
  items: readonly ArtworkWallItem[],
  entriesByArtworkId: CompetitionEntriesByArtworkId,
  studentLabelById: Record<Uuid, string>
): ArtworkWallItem[] =>
  items.map((item) => {
    const studentLabel =
      studentLabelById[item.studentId] ?? item.studentName ?? ""
    const boxLabel = describeEntries(entriesFor(item, entriesByArtworkId))
    const badge = [studentLabel, boxLabel].filter(Boolean).join(" — ")
    if (!badge) return item
    return {
      ...item,
      badge,
      caption: item.caption ? `${badge} — ${item.caption}` : badge,
    }
  })

/**
 * The review queue. Weekday and teacher narrow the pieces first, versions
 * collapse to the newest step, and the rest is grouped into class sections
 * (one per class per calendar day of `timeCreated`). The five-class window
 * counts a class once, across every day it appears. A class is decided only
 * when every piece in it is decided. Pulling the next class in shows all of
 * that class's days.
 *
 * Scope decides what renders, not which classes are in the queue under All
 * and Competition: ALL puts a competition piece with a class in its class
 * section AND in the competition section; ARTWORK drops competition pieces
 * from both the queue and the page; COMPETITION renders only the competition
 * section, drawn from the same window ALL would show. The competition
 * section holds entries from windowed classes, plus class-less entries
 * whose student is enrolled in (or has work in) a windowed class.
 */
export const buildQualityCheckLayout = ({
  items,
  classesById,
  entriesByArtworkId,
  studentLabelById,
  filters,
  hideFinishedItems,
  hideFinishedClasses,
}: QualityCheckLayoutInput): QualityCheckLayout => {
  const narrowed = items.filter(
    (item) =>
      matchesWeekday(item, classesById, filters.weekday) &&
      matchesTeacher(item, classesById, filters.teacherId)
  )
  const { items: latest, versionCounts } = dedupeLatestVersions(narrowed)

  const sectionKeyOf = (item: ArtworkWallItem): string => {
    const classObj = classOf(item, classesById)
    return `${getArtworkDayKey(item.artwork)}|${classObj ? classObj.id : ""}`
  }

  const queueItems = latest.filter((item) => {
    if (!hasCompetitionEntry(item, entriesByArtworkId)) return true
    return (
      filters.scope !== QUALITY_CHECK_SCOPE.ARTWORK &&
      classOf(item, classesById) !== undefined
    )
  })

  const sectionsByKey: Record<string, QualityCheckClassSection> = {}
  for (const item of queueItems) {
    const key = sectionKeyOf(item)
    const existing = sectionsByKey[key]
    if (existing) {
      existing.allItems.push(item)
      continue
    }
    const classObj = classOf(item, classesById)
    sectionsByKey[key] = {
      key,
      dayKey: getArtworkDayKey(item.artwork),
      classId: classObj ? classObj.id : null,
      label: describeQualityCheckClass(classObj),
      allItems: [item],
      items: [],
      decided: false,
    }
  }
  const queue = Object.values(sectionsByKey).map((section) => {
    const allItems = newestFirst(section.allItems)
    return {
      ...section,
      allItems,
      decided: allItems.every((item) => isQualityCheckDecided(item.artwork)),
    }
  })
  queue.sort((a, b) => compareSections(a, b, classesById))

  const classKeyOf = (section: QualityCheckClassSection) =>
    section.classId ?? ""
  const buckets = new Map<
    string,
    { id: string; newestAt: number; decided: boolean }
  >()
  for (const section of queue) {
    const id = classKeyOf(section)
    const newestAt = section.allItems.reduce(
      (max, item) => Math.max(max, item.artwork.timeCreated ?? 0),
      0
    )
    const existing = buckets.get(id)
    if (existing) {
      existing.newestAt = Math.max(existing.newestAt, newestAt)
      existing.decided = existing.decided && section.decided
    } else {
      buckets.set(id, { id, newestAt, decided: section.decided })
    }
  }
  const classOrder = [...buckets.values()].sort((a, b) => {
    if (a.newestAt !== b.newestAt) return b.newestAt - a.newestAt
    const classA = a.id ? classesById[a.id] : undefined
    const classB = b.id ? classesById[b.id] : undefined
    const startA = getClassStartMinutes(classA?.startTime)
    const startB = getClassStartMinutes(classB?.startTime)
    if ((startA === null) !== (startB === null)) return startA === null ? 1 : -1
    if (startA !== null && startB !== null && startA !== startB) {
      return startB - startA
    }
    const byName = (classA?.name ?? "").localeCompare(
      classB?.name ?? "",
      undefined,
      { sensitivity: "base" }
    )
    if (byName !== 0) return byName
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  })
  const windowClassIds = selectQualityCheckClassWindow(
    classOrder.map((bucket) => bucket.id),
    (id) => buckets.get(id)?.decided ?? false
  )
  const windowClassSet = new Set(windowClassIds)
  const windowed = queue.filter((section) =>
    windowClassSet.has(classKeyOf(section))
  )

  const renderedSections =
    filters.scope === QUALITY_CHECK_SCOPE.COMPETITION
      ? []
      : windowed
          .filter((section) => {
            if (!hideFinishedClasses) return true
            return !(buckets.get(classKeyOf(section))?.decided ?? false)
          })
          .map((section) => ({
            ...section,
            // Trim decided pictures only inside a day that still has open
            // work. A day that is itself finished stays intact, so the
            // collapsed card remains. Hide finished classes is what removes
            // a class, and only once every day of it is finished.
            items:
              hideFinishedItems && !section.decided
                ? section.allItems.filter(
                    (item) => !isQualityCheckDecided(item.artwork)
                  )
                : section.allItems,
          }))
          .filter((section) => section.items.length > 0)

  const days: QualityCheckDayGroup[] = []
  for (const section of renderedSections) {
    const day = days.at(-1)
    if (day && day.key === section.dayKey) {
      day.sections.push(section)
    } else {
      days.push({
        key: section.dayKey,
        label: describeQualityCheckDay(section.dayKey),
        sections: [section],
      })
    }
  }

  let competition: QualityCheckCompetitionSection | null = null
  if (filters.scope !== QUALITY_CHECK_SCOPE.ARTWORK) {
    const windowedStudentIds = new Set<Uuid>()
    for (const section of windowed) {
      const classObj = section.classId
        ? classesById[section.classId]
        : undefined
      for (const id of classObj?.studentIds ?? []) windowedStudentIds.add(id)
      for (const item of section.allItems) {
        windowedStudentIds.add(item.studentId)
      }
    }
    const entries = latest.filter((item) => {
      if (!hasCompetitionEntry(item, entriesByArtworkId)) return false
      const classObj = classOf(item, classesById)
      return classObj
        ? windowClassSet.has(classObj.id)
        : windowedStudentIds.has(item.studentId)
    })
    const allItems = badgeCompetitionItems(
      newestFirst(entries),
      entriesByArtworkId,
      studentLabelById
    )
    const decided =
      allItems.length > 0 &&
      allItems.every((item) => isQualityCheckDecided(item.artwork))
    if (allItems.length > 0 && !(decided && hideFinishedClasses)) {
      competition = {
        key: QUALITY_CHECK_COMPETITION_SECTION_KEY,
        allItems,
        decided,
        items:
          hideFinishedItems && !decided
            ? allItems.filter((item) => !isQualityCheckDecided(item.artwork))
            : allItems,
      }
    }
  }

  const renderedItems = uniqBy(
    [
      ...(competition?.items ?? []),
      ...renderedSections.flatMap((section) => section.items),
    ],
    (item) => item.artwork.id
  )

  return {
    competition,
    days,
    renderedItems,
    queuedSectionCount: classOrder.length - windowClassIds.length,
    versionCounts,
  }
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/**
 * `MMYY name` per student, matching `Student.getNameAndDob`, for the judging
 * badges. `formatDob` (not `dayjs` directly) so a missing/garbled dob yields
 * the bare name instead of an invented date.
 */
export const buildStudentLabelIndex = (
  students: readonly Pick<StudentConstructor, "id" | "name" | "dob">[]
): Record<Uuid, string> => {
  const index: Record<Uuid, string> = {}
  for (const student of students) {
    const dob = formatDob(student.dob, STUDENT_DOB_FORMAT)
    index[student.id] = dob ? `${dob} ${student.name}` : student.name
  }
  return index
}

/** Convenience for callers holding arrays. */
export const indexById = <T extends { id: Uuid }>(
  docs: readonly T[]
): Record<Uuid, T> => keyBy(docs, "id")

// ---------------------------------------------------------------------------
// Persisted filters (userSettings.qualityCheckFilters)
// ---------------------------------------------------------------------------

/**
 * Note the semester default is the CURRENT semester, not "All": the read
 * loads a student's whole artwork history, and defaulting the surface to all
 * of it is how "Fall 26" once showed years of pieces. No curriculum by
 * default: an empty selection loads nothing.
 */
export const DEFAULT_QUALITY_CHECK_FILTERS_SETTING: QualityCheckFiltersSetting =
  {
    scope: QUALITY_CHECK_SCOPE.ALL,
    semester: getCurrentSemester(),
    curriculumId: null,
    weekday: null,
    teacherId: null,
    hideFinishedItems: true,
    hideFinishedClasses: false,
  }

const isQualityCheckScope = (value: unknown): value is QualityCheckScope =>
  value === QUALITY_CHECK_SCOPE.ALL ||
  value === QUALITY_CHECK_SCOPE.ARTWORK ||
  value === QUALITY_CHECK_SCOPE.COMPETITION

const nonEmptyString = (value: unknown): string | null =>
  typeof value === "string" && value ? value : null

/**
 * A stored value with a missing/foreign shape is ordinary input (an older
 * build wrote it, or nothing did), so every field falls back independently —
 * same posture as `parseWallPrefs`. The old per-level `byLevel` map is
 * ignored: its class axes were keyed by skill levels, which no longer drive
 * the read.
 */
export const parseQualityCheckFiltersSetting = (
  raw: unknown
): QualityCheckFiltersSetting => {
  if (!isPlainObject(raw)) return DEFAULT_QUALITY_CHECK_FILTERS_SETTING
  const candidate = raw as Partial<
    Record<keyof QualityCheckFiltersSetting, unknown>
  >
  const { weekday } = candidate
  return {
    scope: isQualityCheckScope(candidate.scope)
      ? candidate.scope
      : QUALITY_CHECK_SCOPE.ALL,
    semester: nonEmptyString(candidate.semester) ?? getCurrentSemester(),
    curriculumId: nonEmptyString(candidate.curriculumId),
    weekday:
      typeof weekday === "number" &&
      Number.isInteger(weekday) &&
      weekday >= 0 &&
      weekday <= 6
        ? weekday
        : null,
    teacherId: nonEmptyString(candidate.teacherId),
    hideFinishedItems:
      typeof candidate.hideFinishedItems === "boolean"
        ? candidate.hideFinishedItems
        : DEFAULT_QUALITY_CHECK_FILTERS_SETTING.hideFinishedItems,
    hideFinishedClasses:
      typeof candidate.hideFinishedClasses === "boolean"
        ? candidate.hideFinishedClasses
        : DEFAULT_QUALITY_CHECK_FILTERS_SETTING.hideFinishedClasses,
  }
}
